import { useState, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  FlaskConical,
  Plus,
  Search,
  Truck,
  Star,
  ShieldCheck,
  ArrowRight,
  RefreshCw,
  Layers,
  FileEdit,
  CheckCircle2,
  MessageSquare,
  TrendingUp,
  XCircle,
} from 'lucide-react';
import { cn } from '@/shared/utils/utils';
import { Button } from '@/shared/components/ui/Button';
import { PageHeader } from '@/shared/components/ui/PageHeader';
import { Input } from '@/shared/components/ui/Input';
import { Badge } from '@/shared/components/ui/Badge';
import { Spinner } from '@/shared/components/ui/Spinner';
import { useSampleRequests, useSampleTracking } from '../api';
import { SampleTrackingCard } from '../components/SampleTrackingCard';
import { ROUTES } from '@/routes/paths';
import { SampleDetailDrawer } from '../components/SampleDetailDrawer';
import { SampleFeedbackDialog } from '../components/SampleFeedbackDialog';
import { SampleDispatchModal } from '../components/SampleDispatchModal';
import type { SampleRequest, SampleState } from '../types';

const STATUS_FILTERS: Array<{ id: string; label: string }> = [
  { id: 'all', label: 'All Samples' },
  { id: 'draft', label: 'Draft' },
  { id: 'to_dispatch', label: 'To Dispatch' },
  { id: 'dispatched', label: 'In Transit' },
  { id: 'delivered', label: 'Delivered' },
  { id: 'feedback_received', label: 'Feedback' },
  { id: 'technical_review', label: 'Tech Review' },
  { id: 'converted', label: 'Converted' },
  { id: 'closed_lost', label: 'Closed (Lost)' },
];

export function SamplingPage() {
  const navigate = useNavigate();
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [mineOnly, setMineOnly] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>('');

  // Modals & Drawers
  // ?sample=<id> (a notification about one sample, 2026-10-06) opens it directly.
  const [searchParams] = useSearchParams();
  const [selectedSampleId, setSelectedSampleId] = useState<string | null>(() => searchParams.get('sample'));
  const [activeFeedbackSample, setActiveFeedbackSample] = useState<SampleRequest | null>(null);
  const [activeDispatchSample, setActiveDispatchSample] = useState<SampleRequest | null>(null);

  // Queries
  const {
    data: listData,
    isLoading: loadingList,
    refetch: refetchList,
  } = useSampleRequests({
    status: selectedStatus === 'all' ? undefined : selectedStatus,
    mine: mineOnly,
    limit: 200,
  });

  const {
    data: trackingData,
    isLoading: loadingTracking,
    refetch: refetchTracking,
  } = useSampleTracking(7, mineOnly);

  const samples = listData?.items || [];

  // Filter by search query
  const filteredSamples = useMemo(() => {
    if (!searchQuery.trim()) return samples;
    const q = searchQuery.toLowerCase();
    return samples.filter(
      (s) =>
        s.code?.toLowerCase().includes(q) ||
        s.customerName?.toLowerCase().includes(q) ||
        s.phone?.toLowerCase().includes(q) ||
        s.dispatch?.awb?.toLowerCase().includes(q) ||
        s.dispatch?.courier?.toLowerCase().includes(q) ||
        s.lines?.some((l) => l.productName?.toLowerCase().includes(q) || l.packSize?.toLowerCase().includes(q)),
    );
  }, [samples, searchQuery]);

  const handleRefresh = () => {
    refetchList();
    refetchTracking();
  };

  const getStatusBadge = (status: SampleState) => {
    switch (status) {
      case 'draft':
        return <Badge variant="neutral">Draft</Badge>;
      case 'to_dispatch':
        return <Badge variant="warning">To Dispatch</Badge>;
      case 'dispatched':
        return <Badge variant="info">In Transit</Badge>;
      case 'delivered':
        return <Badge variant="info">Delivered</Badge>;
      case 'feedback_received':
        return <Badge variant="warning">Feedback Received</Badge>;
      case 'technical_review':
        return <Badge className="bg-purple-100 text-purple-800 border-purple-200">Tech Review</Badge>;
      case 'converted':
        return <Badge variant="success">Converted</Badge>;
      case 'closed_lost':
        return <Badge variant="danger">Closed (Lost)</Badge>;
      case 'cancelled':
        return <Badge variant="neutral">Cancelled</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  return (
    <div className="mx-auto max-w-[1580px] space-y-5 pb-16">
      <PageHeader
        icon={FlaskConical}
        eyebrow="Sales"
        title="Sampling & Trials"
        description="Lubricant samples sent to clients — dispatch, customer verdict, technical sign-off and conversion to a quotation."
        actions={<>
          <Button variant="outline" onClick={handleRefresh}>
            <RefreshCw className="h-4 w-4" /> Refresh
          </Button>
          <Button onClick={() => navigate(ROUTES.samplingNew)}>
            <Plus className="h-4 w-4" /> New Sample Request
          </Button>
        </>}
      />

      {/* Metrics & Chase List Component */}
      <SampleTrackingCard
        tracking={trackingData}
        isLoading={loadingTracking}
        onSelectSample={(s) => setSelectedSampleId(s._id)}
        onQuickFeedback={(s) => setActiveFeedbackSample(s)}
      />

      {/* Filters & Search Worksurface */}
      <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm space-y-4">
        <div className="flex flex-col md:flex-row items-center justify-between gap-3">
          {/* Search Box */}
          <div className="relative w-full md:w-80">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search code, customer, product, AWB..."
              className="pl-9 h-9 text-xs"
            />
          </div>

          {/* Scope Controls */}
          <div className="flex items-center gap-2 w-full md:w-auto justify-between md:justify-end">
            <label className="flex items-center gap-2 text-xs font-semibold text-slate-700 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={mineOnly}
                onChange={(e) => setMineOnly(e.target.checked)}
                className="rounded border-slate-300 text-blue-600 focus:ring-blue-500 w-4 h-4"
              />
              Assigned to Me Only
            </label>
          </div>
        </div>

        {/* Status Tabs */}
        <div className="flex items-center flex-wrap gap-x-1 gap-y-2 sm:gap-x-0 text-slate-600 dark:text-slate-400 border-t border-slate-100 pt-3">
          {STATUS_FILTERS.map((f, idx) => {
            let IconComponent = FlaskConical;
            if (f.id === 'all') IconComponent = Layers;
            else if (f.id === 'draft') IconComponent = FileEdit;
            else if (f.id === 'to_dispatch') IconComponent = ArrowRight;
            else if (f.id === 'dispatched') IconComponent = Truck;
            else if (f.id === 'delivered') IconComponent = CheckCircle2;
            else if (f.id === 'feedback_received') IconComponent = MessageSquare;
            else if (f.id === 'technical_review') IconComponent = FlaskConical;
            else if (f.id === 'converted') IconComponent = TrendingUp;
            else if (f.id === 'closed_lost') IconComponent = XCircle;

            const isActive = selectedStatus === f.id;

            return (
              <div key={f.id} className="flex items-center">
                {idx > 0 && (
                  <span className="hidden sm:inline text-slate-300 dark:text-slate-700 mx-3 select-none text-data-xs font-light">&lt;</span>
                )}
                <button
                  type="button"
                  onClick={() => setSelectedStatus(f.id)}
                  className={cn(
                    "inline-flex items-center gap-1.5 text-[13px] font-semibold transition-all duration-150 border-0 bg-transparent py-1 px-2.5 rounded-md hover:text-slate-900 dark:hover:text-slate-100",
                    isActive
                      ? 'text-blue-600 dark:text-blue-400 font-extrabold bg-blue-50/50 dark:bg-blue-950/20'
                      : 'text-muted-foreground'
                  )}
                >
                  <IconComponent className={cn("w-4 h-4", isActive ? "text-blue-600 dark:text-blue-400" : "text-slate-400")} />
                  <span>{f.label}</span>
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Main Samples Data Table */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        {loadingList ? (
          <div className="p-12 flex flex-col items-center justify-center text-slate-400">
            <Spinner className="h-8 w-8" />
            <span className="text-xs font-semibold mt-2">Loading sample requests...</span>
          </div>
        ) : filteredSamples.length === 0 ? (
          <div className="p-12 text-center">
            <FlaskConical className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <h3 className="text-sm font-bold text-slate-700">No Sample Requests Found</h3>
            <p className="text-xs text-slate-400 max-w-sm mx-auto mt-1">
              {searchQuery || selectedStatus !== 'all'
                ? 'No sample records match your search criteria. Try clearing the filters.'
                : 'Click "New Sample Request" above to create your first lubricant sample trial.'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="bg-slate-50 text-slate-500 font-semibold border-b border-slate-200">
                <tr>
                  <th className="p-3.5">Sample Code</th>
                  <th className="p-3.5">Customer & Pipeline</th>
                  <th className="p-3.5">Sample Lines</th>
                  <th className="p-3.5">Dispatch / Courier</th>
                  <th className="p-3.5">Verdict & Tech Sign-Off</th>
                  <th className="p-3.5">Status</th>
                  <th className="p-3.5 text-right">Next Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredSamples.map((sample) => (
                  <tr
                    key={sample._id}
                    className="hover:bg-slate-50/70 transition cursor-pointer"
                    onClick={() => setSelectedSampleId(sample._id)}
                  >
                    {/* Code */}
                    <td className="p-3.5 font-mono font-bold text-blue-700">
                      {sample.code}
                      <span className="block text-data-xs text-slate-400 font-sans font-normal mt-0.5">
                        {new Date(sample.createdAt).toLocaleDateString()}
                      </span>
                    </td>

                    {/* Customer & Anchor */}
                    <td className="p-3.5">
                      <div className="font-bold text-slate-900">{sample.customerName}</div>
                      <div className="text-data-xs text-slate-500 flex items-center gap-1.5 mt-0.5">
                        {sample.dealId ? (
                          <span className="text-blue-600 font-medium truncate max-w-[140px]">
                            Deal: {typeof sample.dealId === 'object' ? sample.dealId.title : 'Active Deal'}
                          </span>
                        ) : sample.leadId ? (
                          <span className="text-indigo-600 font-medium truncate max-w-[140px]">
                            Lead: {typeof sample.leadId === 'object' ? sample.leadId.name : 'Lead'}
                          </span>
                        ) : (
                          <span>Direct Sample</span>
                        )}
                      </div>
                    </td>

                    {/* Lines Summary */}
                    <td className="p-3.5">
                      {sample.lines && sample.lines.length > 0 ? (
                        <div>
                          <span className="font-semibold text-slate-800">
                            {sample.lines[0].productName || 'Lubricant'}
                          </span>
                          <span className="ml-1 text-data-xs text-slate-500">
                            ({sample.lines[0].packSize} × {sample.lines[0].quantity})
                          </span>
                          {sample.lines.length > 1 && (
                            <span className="block text-data-xs text-slate-400 font-medium">
                              +{sample.lines.length - 1} additional product(s)
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="text-slate-400 italic">No lines</span>
                      )}
                    </td>

                    {/* Dispatch */}
                    <td className="p-3.5">
                      {sample.dispatch?.courier ? (
                        <div>
                          <div className="font-medium text-slate-800 flex items-center gap-1">
                            <Truck className="w-3 h-3 text-slate-400" />
                            {sample.dispatch.courier}
                          </div>
                          {sample.dispatch.awb && (
                            <div className="text-data-xs font-mono text-slate-500 mt-0.5">
                              AWB: {sample.dispatch.awb}
                            </div>
                          )}
                          {/* The paper that went with the goods. Findable again
                              after the toast that announced it has gone — which
                              is the only reason the number is stored on the
                              sample at all. */}
                          {sample.sampleInvoiceNumber && sample.sampleInvoiceId && (
                            <a
                              href={`/api/documents/sample-invoice/${sample.sampleInvoiceId}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="mt-0.5 block text-data-xs font-mono text-blue-600 hover:underline"
                            >
                              {sample.sampleInvoiceNumber}
                            </a>
                          )}
                        </div>
                      ) : (
                        <span className="text-slate-400">Not Dispatched</span>
                      )}
                    </td>

                    {/* Verdict & Technical */}
                    <td className="p-3.5">
                      {sample.feedback ? (
                        <div className="space-y-1">
                          <div className="flex items-center gap-1">
                            <span
                              className={`text-data-xs font-bold px-1.5 py-0.5 rounded capitalize ${
                                sample.feedback.outcome === 'approved'
                                  ? 'bg-emerald-100 text-emerald-800'
                                  : sample.feedback.outcome === 'needs_changes'
                                  ? 'bg-amber-100 text-amber-800'
                                  : 'bg-red-100 text-red-800'
                              }`}
                            >
                              {sample.feedback.outcome.replace(/_/g, ' ')}
                            </span>
                            {sample.feedback.rating && (
                              <span className="text-data-xs font-bold text-amber-600 flex items-center">
                                <Star className="w-2.5 h-2.5 fill-amber-400 text-amber-400 mr-0.5" />
                                {sample.feedback.rating}/5
                              </span>
                            )}
                          </div>
                          {sample.technicalApproval?.decision && (
                            <div className="text-data-xs text-purple-700 font-semibold flex items-center gap-1">
                              <ShieldCheck className="w-3 h-3" /> Tech:{' '}
                              {sample.technicalApproval.decision}
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="text-slate-400 text-data-xs">—</span>
                      )}
                    </td>

                    {/* Status */}
                    <td className="p-3.5">{getStatusBadge(sample.status)}</td>

                    {/* Next Action Button */}
                    <td
                      className="p-3.5 text-right"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {sample.availableActions?.includes('to_dispatch') && (
                        <Button
                          size="sm"
                          className="h-7 text-xs bg-indigo-600 hover:bg-indigo-700 text-white"
                          onClick={() => {
                            setActiveDispatchSample(sample);
                          }}
                        >
                          To Dispatch
                        </Button>
                      )}
                      {sample.availableActions?.includes('dispatch') && (
                        <span className="text-data-xs text-slate-500 italic">
                          Awaiting Dispatch team
                        </span>
                      )}
                      {sample.availableActions?.includes('record_feedback') && (
                        <Button
                          size="sm"
                          className="h-7 text-xs bg-amber-600 hover:bg-amber-700 text-white"
                          onClick={() => {
                            setActiveFeedbackSample(sample);
                          }}
                        >
                          Feedback
                        </Button>
                      )}
                      {sample.availableActions?.includes('convert_to_quotation') && (
                        <Button
                          size="sm"
                          className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700 text-white"
                          onClick={() => setSelectedSampleId(sample._id)}
                        >
                          Convert
                        </Button>
                      )}
                      {!sample.availableActions?.includes('dispatch') &&
                        !sample.availableActions?.includes('to_dispatch') &&
                        !sample.availableActions?.includes('record_feedback') &&
                        !sample.availableActions?.includes('convert_to_quotation') && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 text-xs text-slate-500"
                            onClick={() => setSelectedSampleId(sample._id)}
                          >
                            View <ArrowRight className="w-3 h-3 ml-1" />
                          </Button>
                        )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Detail Slide-over Drawer */}
      <SampleDetailDrawer
        sampleId={selectedSampleId}
        open={Boolean(selectedSampleId)}
        onClose={() => setSelectedSampleId(null)}
      />

      {/* Quick Dispatch Modal */}
      {activeDispatchSample && (
        <SampleDispatchModal
          open={Boolean(activeDispatchSample)}
          onClose={() => setActiveDispatchSample(null)}
          sample={activeDispatchSample}
        />
      )}

      {/* Quick Feedback Dialog */}
      {activeFeedbackSample && (
        <SampleFeedbackDialog
          open={Boolean(activeFeedbackSample)}
          onClose={() => setActiveFeedbackSample(null)}
          sample={activeFeedbackSample}
        />
      )}
    </div>
  );
}
